export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

// Format a stored/E.164 number for display, e.g. "+16315765805" → "1 (631)
// 576-5805" (IMP-361). Falls back to the raw value when it isn't a US 10/11-
// digit number, so an unexpected format is never mangled.
export function formatPhoneDisplay(value: string | null | undefined): string {
  if (!value) return ""
  let digits = value.replace(/\D/g, "")
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1)
  if (digits.length !== 10) return value
  return `1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

// Format a stored/E.164 US number as a bare national number, e.g.
// "+16315765805" → "(631) 576-5805". Like formatPhoneDisplay but without the
// leading "1 " — used where a clean, scannable list of numbers reads best
// (IMP-430, the leads table). Falls back to the raw value for a non-US-shaped
// number so it's never mangled.
export function formatPhoneNational(value: string | null | undefined): string {
  if (!value) return ""
  let digits = value.replace(/\D/g, "")
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1)
  if (digits.length !== 10) return value
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

// Whether a free-form phone string is a complete US number — 10 digits, or 11
// with a leading country-code 1. Mirrors the server contract (to_e164) so the
// wizard blocks a malformed "Transfer to" number before it's saved (IMP-345).
export function isValidUsPhoneNumber(
  value: string | null | undefined,
): boolean {
  const digits = (value ?? "").replace(/\D/g, "")
  return (
    digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))
  )
}

// Pulls the 3-digit area code out of a free-form phone string. Handles
// the common US formats: "(415) 555-1234", "415-555-1234",
// "+1 415 555 1234". Returns "" when the input doesn't look like a US
// number (too few digits, non-1 country code, etc.) so the Connect step
// just shows an empty field instead of a garbage prefill.
export function areaCodeFromPhone(phone: string | null | undefined): string {
  if (!phone) return ""
  let digits = phone.replace(/\D/g, "")
  // Strip the US country code so "+1 415 …" gives back "415", not "141".
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1)
  }
  if (digits.length < 10) return ""
  return digits.slice(0, 3)
}
